import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const { FiShoppingBag, FiExternalLink, FiStar, FiZap, FiTarget, FiTrendingUp, FiAward, FiCheckCircle, 
  FiArrowRight, FiUsers, FiDollarSign, FiBarChart3, FiCpu, FiBook, FiMessageCircle, FiLock, 
  FiUnlock, FiShield } = FiIcons

const ProductRecommendations = () => {
  const [loading, setLoading] = useState(true)
  const [selectedProduct, setSelectedProduct] = useState(null)

  // Sample products - in real app this would come from admin-managed database
  const products = [
    {
      id: 'biz-ai-coach',
      name: 'Biz AI Coach',
      tagline: 'Your Personal Business Mentor',
      description: 'Transform your business with personalized AI coaching that adapts to your industry, goals, and challenges.',
      longDescription: 'The Biz AI Coach is your 24/7 business mentor, designed to help entrepreneurs and business owners scale their operations, optimize strategies, and overcome challenges. Using advanced AI technology, it provides personalized guidance based on your specific industry, business model, and goals.',
      logo: 'https://assets.cdn.filesafe.space/2LqsTomEY3T009LGB5ZG/media/671cfed4b62b363560d3d2a7.png',
      image: 'https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/2LqsTomEY3T009LGB5ZG/media/683c7043dfd9e97ed193b458.jpeg',
      appLink: 'https://bizunderworld.my-ai.coach',
      websiteLink: 'https://bizunderworld.com/ai-coach',
      price: '$37.99/month',
      originalPrice: '$47.99/month',
      rating: 4.9,
      reviews: 1247,
      category: 'Business Coaching',
      featured: true,
      problems: [
        'Struggling to scale your business effectively',
        'Lack of strategic direction and clear roadmap',
        'Difficulty making data-driven decisions',
        'Overwhelming business challenges without guidance',
        'Limited access to expensive business consultants'
      ],
      benefits: [
        '24/7 personalized business coaching and guidance',
        'Industry-specific strategies and best practices',
        'Data-driven insights and actionable recommendations',
        'Cost-effective alternative to traditional consulting',
        'Scalable advice that grows with your business',
        'Instant access to business expertise and knowledge'
      ],
      features: [
        'Adaptive Intelligence',
        'Multi-Modal Guidance',
        'Pattern Recognition',
        'Financial Planning Tools',
        'Market Analysis & Insights',
        'Competitive Intelligence',
        'Performance Tracking',
        'Strategic Planning Assistant'
      ],
      guaranteeText: 'Our Underground Guarantee: If you don\'t experience at least three meaningful insights that help you see yourself or your challenges differently within your first 30 days, we\'ll refund your subscription completely. We\'re that confident in the value Biz AI Coach will bring to your personal development journey.',
      specialOffer: 'Submit a video review of your experience and get $10 off your subscription.',
      featureDescriptions: {
        'Adaptive Intelligence': 'Unlike static tools, Biz AI Coach learns your patterns and adapts its approach to your unique needs and growth edges.',
        'Multi-Modal Guidance': 'Seamlessly shifts between coach, mentor, and teacher roles based on what will create the most value in each moment.',
        'Pattern Recognition': 'Identifies connections across different conversations and life domains that would be impossible to see on your own.'
      },
      testimonials: [
        {
          name: 'Sarah Johnson',
          role: 'CEO, TechStart Inc.',
          content: 'The Biz AI Coach helped me identify growth opportunities I never saw before. Revenue increased 40% in 3 months.',
          rating: 5
        },
        {
          name: 'Michael Chen',
          role: 'Small Business Owner',
          content: 'Finally, affordable business coaching that actually understands my industry. Game-changer for my operations.',
          rating: 5
        }
      ]
    }
  ]

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setLoading(false), 1000)
  }, [])

  const ProductCard = ({ product }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-purple-400/30 overflow-hidden hover:shadow-dominance transition-all duration-500"
    >
      {/* Featured Badge */}
      {product.featured && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-gradient-to-r from-forge-400 to-orange-500 text-shadow-950 px-3 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
            <SafeIcon icon={FiStar} className="text-xs" />
            <span>FEATURED</span>
          </div>
        </div>
      )}

      {/* Product Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-shadow-900/80 to-transparent"></div>
        
        {/* Logo Overlay */}
        <div className="absolute bottom-4 left-4">
          <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-white/20 bg-white/10 backdrop-blur-sm">
            <img 
              src={product.logo} 
              alt={`${product.name} logo`} 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-purple-300">{product.name}</h3>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiStar} className="text-forge-400 text-sm" />
              <span className="text-forge-400 font-semibold text-sm">{product.rating}</span>
              <span className="text-forge-300 text-xs">({product.reviews})</span>
            </div>
          </div>
          <p className="text-purple-400 font-medium text-sm mb-2">{product.tagline}</p>
          <p className="text-forge-300 text-sm line-clamp-2">{product.description}</p>
        </div>

        {/* Pricing */}
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-forge-400">{product.price}</span>
            {product.originalPrice && (
              <span className="text-sm text-forge-300 line-through">{product.originalPrice}</span>
            )}
            <div className="bg-danger-500/20 text-danger-400 px-2 py-1 rounded-full text-xs font-semibold">
              LIMITED OFFER
            </div>
          </div>
          <div className="mt-1 text-xs text-green-400">
            {product.specialOffer}
          </div>
        </div>

        {/* Key Benefits Preview */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-purple-400 mb-2">Key Benefits:</h4>
          <div className="space-y-1">
            {product.benefits.slice(0, 3).map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm text-forge-300">
                <SafeIcon icon={FiCheckCircle} className="text-success-400 text-xs flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
            {product.benefits.length > 3 && (
              <div className="text-xs text-purple-400 font-medium">
                +{product.benefits.length - 3} more benefits
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3">
          <a 
            href={product.appLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <span>Start Coaching Now</span>
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </a>
          
          <div className="flex space-x-2">
            <button 
              onClick={() => setSelectedProduct(product)}
              className="flex-1 bg-shadow-800/50 text-purple-400 py-2 px-3 rounded-lg font-medium hover:bg-shadow-700/50 transition-all duration-300 flex items-center justify-center space-x-2 border border-purple-400/30"
            >
              <span>Learn More</span>
              <SafeIcon icon={FiArrowRight} className="text-sm" />
            </button>
            <a 
              href={product.websiteLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-3 py-2 bg-forge-400/20 text-forge-400 rounded-lg font-medium hover:bg-forge-400/30 transition-all duration-300 border border-forge-400/30"
            >
              <SafeIcon icon={FiExternalLink} className="text-sm" />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const ProductDetailModal = ({ product, onClose }) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-3xl shadow-dominance max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-purple-400/30"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/20">
                <img 
                  src={product.logo} 
                  alt={`${product.name} logo`} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{product.name}</h2>
                <p className="text-purple-200">{product.tagline}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{product.rating}</div>
              <div className="flex items-center justify-center space-x-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <div className="text-purple-200 text-sm">{product.reviews} reviews</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold">{product.price}</div>
              {product.originalPrice && (
                <div className="text-purple-200 line-through text-sm">{product.originalPrice}</div>
              )}
              <div className="text-purple-200 text-sm">per month</div>
            </div>

            <div className="text-center">
              <div className="text-sm text-green-300 font-bold">{product.specialOffer}</div>
              <div className="text-purple-200 text-sm mt-1">Limited Time</div>
              <div className="text-purple-200 text-sm">Discount</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Description */}
          <div>
            <h3 className="text-xl font-bold text-purple-400 mb-4">About This Product</h3>
            <p className="text-forge-200 leading-relaxed">{product.longDescription}</p>
          </div>

          {/* Problems & Solutions */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-bold text-danger-400 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiTarget} />
                <span>Problems We Solve</span>
              </h3>
              <div className="space-y-3">
                {product.problems.map((problem, index) => (
                  <div key={index} className="flex items-start space-x-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-danger-400 mt-2 flex-shrink-0"></div>
                    <span className="text-forge-300">{problem}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-success-400 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} />
                <span>Benefits You Get</span>
              </h3>
              <div className="space-y-3">
                {product.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-2 text-sm">
                    <SafeIcon icon={FiCheckCircle} className="text-success-400 mt-0.5 flex-shrink-0" />
                    <span className="text-forge-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Features with Descriptions */}
          <div>
            <h3 className="text-lg font-bold text-forge-400 mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiZap} />
              <span>Key Features</span>
            </h3>
            <div className="space-y-4">
              {Object.entries(product.featureDescriptions).map(([feature, description], index) => (
                <div key={index} className="bg-shadow-800/50 p-4 rounded-lg border border-forge-400/20">
                  <h4 className="text-forge-400 font-bold mb-1">{feature}</h4>
                  <p className="text-forge-300 text-sm">{description}</p>
                </div>
              ))}
              {product.features.filter(f => !Object.keys(product.featureDescriptions).includes(f)).map((feature, index) => (
                <div key={`other-${index}`} className="flex items-center space-x-2 text-sm bg-shadow-800/30 p-3 rounded-lg">
                  <SafeIcon icon={FiZap} className="text-forge-400 flex-shrink-0" />
                  <span className="text-forge-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Guarantee */}
          <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 rounded-2xl p-6 border border-green-400/30">
            <h3 className="text-lg font-bold text-green-400 mb-3 flex items-center space-x-2">
              <SafeIcon icon={FiShield} />
              <span>Our Guarantee</span>
            </h3>
            <p className="text-green-300 text-sm leading-relaxed">
              {product.guaranteeText}
            </p>
          </div>

          {/* Testimonials */}
          <div>
            <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiUsers} />
              <span>What Users Say</span>
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {product.testimonials.map((testimonial, index) => (
                <div key={index} className="bg-shadow-800/50 p-4 rounded-xl border border-purple-400/20">
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-sm" />
                    ))}
                  </div>
                  <p className="text-forge-300 text-sm mb-3 italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold text-purple-400 text-sm">{testimonial.name}</div>
                    <div className="text-purple-300 text-xs">{testimonial.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl p-6 border border-purple-400/30">
            <div className="text-center">
              <h3 className="text-xl font-bold text-purple-300 mb-2">Ready to Transform Your Business?</h3>
              <p className="text-purple-400 mb-6">Join thousands of entrepreneurs who are already scaling with AI coaching</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={product.appLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Start Your Journey</span>
                  <SafeIcon icon={FiExternalLink} />
                </a>
                <a 
                  href={product.websiteLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border border-purple-400/50 text-purple-400 px-8 py-3 rounded-xl font-semibold hover:bg-purple-400/10 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Learn More</span>
                  <SafeIcon icon={FiArrowRight} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-purple-400/30 border-t-purple-400 rounded-full animate-spin mb-4"></div>
          <h3 className="text-xl font-bold text-purple-400 mb-2">Loading Recommendations</h3>
          <p className="text-purple-300">Finding the perfect tools for your success...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dominance-gradient">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiShoppingBag} className="text-2xl text-purple-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent">
              Product Recommendations
            </h1>
          </div>
          <p className="text-xl text-purple-300 max-w-2xl mx-auto mb-6">
            Curated tools and services to accelerate your success and dominate your goals
          </p>
          
          {/* Admin Notice */}
          <div className="bg-gradient-to-r from-forge-900/20 to-orange-900/20 rounded-xl p-4 border border-forge-400/30 max-w-lg mx-auto">
            <div className="flex items-center justify-center space-x-2 text-forge-400">
              <SafeIcon icon={FiLock} className="text-sm" />
              <span className="text-sm font-medium">Admin-Curated Products</span>
            </div>
            <p className="text-xs text-forge-300 mt-1">
              Only verified, high-quality products recommended by our team
            </p>
          </div>
        </motion.div>

        {/* Products Grid */}
        <div className="grid gap-8 mb-12">
          {products.map((product, index) => (
            <div key={product.id}>
              <ProductCard product={product} />
            </div>
          ))}
        </div>

        {/* Coming Soon Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-8 text-center"
        >
          <div className="w-16 h-16 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-power">
            <SafeIcon icon={FiZap} className="text-shadow-950 text-2xl" />
          </div>
          <h3 className="text-2xl font-bold text-forge-400 mb-4">More Products Coming Soon</h3>
          <p className="text-forge-300 mb-6 max-w-lg mx-auto">
            We're constantly evaluating and adding new tools to help you achieve your goals faster and more efficiently.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-forge-300">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiTarget} className="text-forge-400" />
              <span>Productivity Tools</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiTrendingUp} className="text-forge-400" />
              <span>Growth Platforms</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiBarChart3} className="text-forge-400" />
              <span>Analytics Tools</span>
            </div>
          </div>
        </motion.div>

        {/* Product Detail Modal */}
        <AnimatePresence>
          {selectedProduct && (
            <ProductDetailModal
              product={selectedProduct}
              onClose={() => setSelectedProduct(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default ProductRecommendations